/**
 * Typed UI strings for web/desktop. Messages come pre-parsed from tools/codegen (src/generated/messages.ts), so the
 * runtime only formats: intl-messageformat with the locale's CLDR plural rules and number formatting.
 * Bundlers can alias "@formatjs/icu-messageformat-parser" to "@formatjs/icu-messageformat-parser/no-parser.js"
 * (the parser is never called at runtime), which cuts intl-messageformat from ~10 KB to ~2.6 KB gzip.
 */
import { IntlMessageFormat } from "intl-messageformat";
import { defaultLocale, locales, messages, type Locale, type MessageKey, type MessageParams } from "./generated/messages.ts";

export { defaultLocale, locales };
export type { Locale, MessageKey, MessageParams };

/** `params` is optional for keys without placeholders and required (and typed) otherwise. */
export type FormatArgs<K extends MessageKey> = keyof MessageParams[K] extends never ? [params?: MessageParams[K]] : [params: MessageParams[K]];

type Ast = Exclude<ConstructorParameters<typeof IntlMessageFormat>[0], string>;
const formatters = new Map<string, IntlMessageFormat>();

/** Renders `key` in `locale`: `{name}` placeholders, `{count, plural, …}` (CLDR categories, `#` = grouped number). */
export function format<K extends MessageKey>(locale: Locale, key: K, ...[params]: FormatArgs<K>): string {
  // A key this build doesn't have (e.g. a server-sent id newer than a stale tab's bundle) falls back to the default
  // locale, then to the key itself: a missing string must never take the screen down.
  let source = locale;
  let message = messages[locale][key];
  if (message === undefined) {
    source = defaultLocale;
    message = messages[defaultLocale][key];
    if (message === undefined) {
      console.warn("i18n: unknown message key", key);
      return String(key);
    }
  }
  if (typeof message === "string") return message;
  const id = `${source}\u0000${key}`;
  let f = formatters.get(id);
  if (!f) {
    f = new IntlMessageFormat(message as unknown as Ast, source);
    formatters.set(id, f);
  }
  return f.format<string>(params as Record<string, string | number> | undefined) as string;
}

/** `t(key, params)` bound to one locale — what components use (`const t = translator(locale)`). */
export function translator(locale: Locale) {
  return <K extends MessageKey>(key: K, ...args: FormatArgs<K>): string => format(locale, key, ...args);
}

/**
 * Best supported locale for a preference list (e.g. `navigator.languages`, `Accept-Language` tags):
 * exact tag match first, then same language (`pt`, `pt-PT` → `pt-BR`), else the default (`en`).
 */
export function resolveLocale(languages: readonly string[]): Locale {
  const norm = (tag: string) => tag.trim().replace(/_/g, "-").toLowerCase();
  for (const tag of languages) {
    const t = norm(tag);
    if (!t) continue;
    const exact = locales.find((l) => l.toLowerCase() === t);
    if (exact) return exact;
    const lang = t.split("-")[0];
    const sameLanguage = locales.find((l) => l.toLowerCase().split("-")[0] === lang);
    if (sameLanguage) return sameLanguage;
  }
  return defaultLocale;
}
